/**
 * Chat Service for handling communication with the chat API
 * Provides methods for sending messages and retrieving chat history
 */
import api from './api';

const chatService = {
  /**
   * Send a message to the chat API
   * @param {string} question - The user's question
   * @param {number} topK - Number of context chunks to retrieve (default: 5)
   * @param {string} userId - Optional user ID for personalization
   * @returns {Promise} - Promise containing the API response
   */
  sendMessage: async (question, topK = 5, userId = null) => {
    try {
      // Create request payload - backend now handles both JSON and form data formats
      const requestData = {
        question: question,
        embedding: [], // Empty array, backend will handle embedding
        top_k: topK,
        // Only send user_id if it's a valid UUID
        user_id: userId && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(userId) ? userId : null,
        session_id: localStorage.getItem('sessionId') || null
      };

      // Send request through centralized API instance (will use /api/chat/ask)
      const response = await api.post('/chat/ask', requestData, {
        headers: {
          'Content-Type': 'application/json',
        },
        // Add timeout to prevent hanging requests
        timeout: 30000, // 30 seconds
      });

      // Log successful request for debugging
      if (process.env.REACT_APP_DEBUG_API_CALLS === 'true') console.debug('Chat request completed');
      return response.data;
    } catch (error) {
      // Enhanced error logging with more details
      console.error('Chat request failed', { status: error.response?.status });
      throw error;
    }
  },

  /**
   * Get chat history for a user (placeholder for future implementation)
   * @param {string} userId - The user ID to get history for
   * @returns {Promise} - Promise containing chat history
   */
  getChatHistory: async (userId) => {
    try {
      // This would be implemented when a chat history endpoint is available
      const response = await api.get(`/chat/history?user_id=${encodeURIComponent(userId)}`);
      return response.data;
    } catch (error) {
      console.error('Chat history request failed', { status: error.response?.status });
      throw error;
    }
  }
};

export default chatService;
